import { useState, useEffect } from 'react';
import Button from './ui/Button';
import { X, Download } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOSBanner, setIsIOSBanner] = useState(false);

  useEffect(() => {
    // iOS Add to Home Screen guidance (no beforeinstallprompt on iOS)
    const isStandalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (window as any).navigator.standalone;
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const dismissedUntil = localStorage.getItem('pwa-install-dismissed-until');
    const now = Date.now();
    const isSuppressed = dismissedUntil ? now < Number(dismissedUntil) : false;

    if (isIOS && !isStandalone && !isSuppressed) {
      setIsIOSBanner(true);
      setShowPrompt(true);
    }

    const handler = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      setIsIOSBanner(false);
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (!isSuppressed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler as any);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler as any);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt for reuse
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal for 7 days (store absolute expiry)
    const dismissUntil = Date.now() + 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem('pwa-install-dismissed-until', dismissUntil.toString());
  };

  if (!showPrompt) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '90px',
        left: '1rem',
        right: '1rem',
        backgroundColor: 'var(--color-card)',
        border: '2px solid var(--color-accent)',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 8px 32px rgba(196, 255, 0, 0.2)',
        zIndex: 1000,
        animation: 'slideUp 0.3s ease-out',
      }}
    >
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          background: 'none',
          border: 'none',
          color: 'var(--color-text)',
          cursor: 'pointer',
          padding: '0.25rem',
        }}
        aria-label="Dismiss"
      >
        <X size={20} />
      </button>

      <div style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '0.25rem', color: 'var(--color-secondary)' }}>
          Install Ember App
        </h3>
        {isIOSBanner ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text)', opacity: 0.8 }}>
            On iPhone: tap Share, then Add to Home Screen to install.
          </p>
        ) : (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text)', opacity: 0.8 }}>
            Add to your home screen for quick access and offline use
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {!isIOSBanner && (
          <Button
            onClick={handleInstall}
            variant="primary"
            iconLeft={<Download size={18} />}
            style={{ flex: 1 }}
          >
            Install
          </Button>
        )}
        <Button
          onClick={handleDismiss}
          variant="secondary"
        >
          Not Now
        </Button>
      </div>
    </div>
  );
}

